import React, { useRef } from 'react'
import { DragObjectWithType, useDrop } from "react-dnd-11"
import { Transforms, Editor, Element, Path } from 'slate'
import { ReactEditor } from 'slate-react'
import { GridDrop, GridDropLeft } from '../../elements/Element/grid'
import { isEqual } from "lodash"
import { INDENT_SIZE } from '../../../../../utils/common-rte-utils/constants'
//@ts-ignore
import styles from './style.module.css'


declare interface ICustomItem extends DragObjectWithType {
  path: any,
  dropped: any,
  canDrop: any,
  element?: any,
}

/**
 * Compute the destination path for a block drag-and-drop reorder.
 *
 * The dragged node is placed as a SIBLING of the block it is dropped on
 * (`destinationPath`), immediately before it (`before`) or after it. This is the
 * behaviour the drop indicator line shows the user, and it works uniformly for:
 *  - reordering blocks at the same level (e.g. moving an image between paragraphs),
 *  - dropping a block INTO a list — the node becomes a child of the list at the
 *    hovered position and the list normalizer wraps it into an `<li>`, i.e. an
 *    image can legitimately become a list item.
 *
 * The previous implementation compared paths with `path.join('')`, a string
 * comparison that breaks for multi-digit indices ("2" < "11" === false) and for
 * nested paths, so the target index was often wrong — landing the image at the
 * wrong slot or in an invalid position (duplicate drag icons / lost image).
 *
 * Returns `null` when the move must be cancelled and the node left exactly where
 * it is, i.e. when:
 *  - either path is empty / the source is dropped onto itself,
 *  - the destination is inside the source's own subtree (would move a node into
 *    its own descendant), or
 *  - the node is already immediately before/after the target (a no-op move).
 *
 * `Transforms.moveNodes` uses an internal `pathRef` for `to`, so it compensates
 * for the shift caused by removing the source; the index adjustments below match
 * slate 0.77 semantics (verified empirically).
 */
export const getBlockDropTarget = (sourcePath: Path, destinationPath: Path, before: boolean): Path | null => {
  if (!sourcePath?.length || !destinationPath?.length) return null
  if (Path.equals(sourcePath, destinationPath)) return null
  // never move a node into its own subtree
  if (Path.isAncestor(sourcePath, destinationPath)) return null

  const destParent = Path.parent(destinationPath)
  const sameParent = Path.equals(Path.parent(sourcePath), destParent)
  const srcIndex = sourcePath[sourcePath.length - 1]
  const destIndex = destinationPath[destinationPath.length - 1]

  let toIndex: number
  if (before) {
    if (sameParent && srcIndex < destIndex) {
      if (srcIndex === destIndex - 1) return null // already immediately before target
      toIndex = destIndex - 1
    } else {
      toIndex = destIndex
    }
  } else {
    if (sameParent && srcIndex > destIndex) {
      if (srcIndex === destIndex + 1) return null // already immediately after target
      toIndex = destIndex + 1
    } else {
      toIndex = destIndex + 1
    }
  }
  return [...destParent, toIndex]
}

export const DndBlock = (props) => {
  const { children, path, editor, collection, element, disablegriddnd, setTriggerRerenderingAfterDnd } = props
  const accept = collection ? collection : 'doc'
  const dndBlockRef = useRef(null)
  
  // Calculate marginLeft from data-indent-level
  const indentLevel = element?.attrs?.['data-indent-level'] || 0
  const calculatedMarginLeft = indentLevel > 0 ? `${indentLevel * INDENT_SIZE}px` : null

  const recalculatePath = (slateElement: any, fallbackPath: any) => {
    if (!slateElement) return fallbackPath
    const recalculatedPath = ReactEditor.findPath(editor as ReactEditor, slateElement)
    return recalculatedPath && recalculatedPath.length > 0 ? recalculatedPath : fallbackPath
  }
  const [{ over }, drop] = useDrop({
    accept: accept,
    collect: (monitor) => {
      const isCurrentOver = monitor.isOver({ shallow: true })
      if (!isCurrentOver && props.singleLineRef?.current) {

        if (!monitor.canDrop()) {
          props.singleLineRef.current.style['opacity'] = '0'
          props.singleLineRef.current.style['transition'] = 'none'
        }
      }
      return {
        over: isCurrentOver
      }
    },
    hover: (item: ICustomItem, monitor) => {
      const sourcePath = recalculatePath(item?.element, item?.path || null)
      const destinationPath = recalculatePath(element, path)
      
      if (accept === item.type) {
        if (JSON.stringify(destinationPath) === JSON.stringify(sourcePath)) {
          return {}
        }
        if (sourcePath && sourcePath.length < destinationPath.length) {
          let sameLevelPath = destinationPath.slice(0, sourcePath.length)
          if (isEqual(sameLevelPath, sourcePath)) {
            return {}
          }
        }
        const clientOffset = monitor.getClientOffset()
        if (dndBlockRef.current && clientOffset) {
          const hoverBoundingRect = dndBlockRef.current.getBoundingClientRect()
          const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
          const hoverClientY = clientOffset.y - hoverBoundingRect.top
          if (hoverClientY <= hoverMiddleY) {
            props.singleLineRef.current.style['width'] = `${hoverBoundingRect.right - hoverBoundingRect.left}px`;
            props.singleLineRef.current.style['left'] = `${hoverBoundingRect.left}px`;
            props.singleLineRef.current.style['top'] = `${hoverBoundingRect.top}px`;
            props.singleLineRef.current.style['opacity'] = '1'

          } else {
            props.singleLineRef.current.style['width'] = `${hoverBoundingRect.right - hoverBoundingRect.left}px`;
            props.singleLineRef.current.style['left'] = `${hoverBoundingRect.left}px`;
            props.singleLineRef.current.style['top'] = `${hoverBoundingRect.bottom}px`
            props.singleLineRef.current.style['opacity'] = '1'
          }
        }
      }
    },
    drop: (item, monitor) => {
      if (!item.dropped && item.canDrop && dndBlockRef.current) {
        // Any failure here must leave the dragged node exactly where it started
        // so its content (e.g. an image) can never be lost or duplicated.
        try {
          const sourcePath = recalculatePath(item?.element, item?.path || null)
          const destinationPath = recalculatePath(element, path)

          const hoverBoundingRect = dndBlockRef.current.getBoundingClientRect()
          const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
          const clientOffset = monitor.getClientOffset()
          const hoverClientY = clientOffset.y - hoverBoundingRect.top
          const before = hoverClientY <= hoverMiddleY

          const to = getBlockDropTarget(sourcePath, destinationPath, before)
          if (to) {
            Transforms.moveNodes(editor, { at: sourcePath, to })
            // Reposition the caret onto the dropped node. Its resting path is `to`
            // adjusted for the removal of the source (and for any normalization,
            // e.g. being wrapped into an <li>). Selection is cosmetic and must
            // never abort the completed move, so it is fully best-effort.
            try {
              // `node` is required by the operation type but unused by path math.
              const restPath = Path.transform(to, { type: 'remove_node', path: sourcePath, node: { text: '' } })
              if (restPath && Editor.hasPath(editor, restPath)) {
                Transforms.select(editor, Editor.start(editor, restPath))
              }
            } catch {
              /* best-effort selection only */
            }
            setTriggerRerenderingAfterDnd(Date.now())
          }
        } catch (error) {
          console.error('SCRTE DnD: drop cancelled, node kept at original position', error)
        }
      }
      item.dropped = true
    },
  })

  return (
    <div data-testid={'dnd-block'} className={styles['p-relative']} >
      <div
        ref={drop}
        style={{ 
          paddingLeft: calculatedMarginLeft ? `calc(30px + ${calculatedMarginLeft})` : '30px', 
          marginLeft: '-30px',
          ...(props.styles || {})
        }}
      >
        <div ref={dndBlockRef} className={(over && Element.isElement(element) && editor.isVoid(element)) ? styles['div-block-action'] : ''}>
          {children}
        </div>
      </div>
      {!disablegriddnd && <GridDropLeft editor={editor} element={element} accept={accept} />}
      {!disablegriddnd && <GridDrop editor={editor} element={element} accept={accept} />}
    </div>
  )
}
